import sendAnalytics, { ORGANIZATION_TYPE } from 'igentify-node-shared/dist/services/analytics';
import { decodeJwt, isMobile, useWorkflowManager } from '@/shared/helpers/helpers';
import { EventTypes } from '@/shared/analytics/types';
import {
  AccessFlowEnterDetailsEventPayload,
  AnalyticsEventPayload,
  AskedHelpEventPayload,
  ClickedQuizAnswerEventPayload,
  PageViewEventPayload,
  ReadFAQArticleEventPayload,
  StartVideoPlayingEventPayload,
  WatchedVideoEventPayload,
  WatchedEnoughPercentageEventPayload,
  UserLoginEventPayload,
} from '@/shared/analytics/eventPayloadTypes';
import { CompilerState } from '@/shared/types';
import loginModule from '@/modules/login/shared/store';
import { ILoginStore } from '@/modules/login/shared/store/shared.types';
import { getRainbowClinicalIndication, getFriendlyInvitationType } from '@/shared/analytics/helpers';
import { authService, storageService } from '@/shared/services';
import { isDevMode } from '@/shared/helpers/utils';
import { configs } from '@/shared/configs';

function sendAnalyticsEvent(eventType: EventTypes.USER_LOGIN, payload: UserLoginEventPayload): Promise<string>;
function sendAnalyticsEvent(eventType: EventTypes.ACCESS_FLOW_ENTER_DETAILS, payload: AccessFlowEnterDetailsEventPayload): Promise<string>;
function sendAnalyticsEvent(eventType: EventTypes.PAGE_VIEW, payload: PageViewEventPayload): Promise<string>;
function sendAnalyticsEvent(eventType: EventTypes.START_VIDEO, payload: StartVideoPlayingEventPayload): Promise<string>;
function sendAnalyticsEvent(eventType: EventTypes.WATCHED_VIDEO, payload: WatchedVideoEventPayload): Promise<string>;
function sendAnalyticsEvent(eventType: EventTypes.WATCHED_25_PERCENT_OF_VIDEO, payload: WatchedEnoughPercentageEventPayload): Promise<string>;
function sendAnalyticsEvent(eventType: EventTypes.WATCHED_50_PERCENT_OF_VIDEO, payload: WatchedEnoughPercentageEventPayload): Promise<string>;
function sendAnalyticsEvent(eventType: EventTypes.WATCHED_75_PERCENT_OF_VIDEO, payload: WatchedEnoughPercentageEventPayload): Promise<string>;
function sendAnalyticsEvent(eventType: EventTypes.OPEN_COUNSELING_REPORT): Promise<string>;
function sendAnalyticsEvent(eventType: EventTypes.CLICKED_QUIZ_QUESTION, payload: ClickedQuizAnswerEventPayload): Promise<string>;
function sendAnalyticsEvent(eventType: EventTypes.ASKED_HELP, payload: AskedHelpEventPayload): Promise<string>;
function sendAnalyticsEvent(eventType: EventTypes.READ_FAQ_ARTICLE, payload: ReadFAQArticleEventPayload): Promise<string>;
function sendAnalyticsEvent(eventType: EventTypes, payload?: AnalyticsEventPayload): Promise<string> {
  const token = storageService.session.get(configs.AUTH_TOKEN);

  if (authService.isControlledMode()) {
    return Promise.resolve('Controlled mode, not sending analytics event');
  }

  if (!token) {
    return Promise.resolve('No Authorization method for analytics was provided.');
  }

  const accessSessionPayload = useWorkflowManager().compiler.getState as CompilerState;
  const personalDetails = accessSessionPayload.data?.['personal-details'];
  const organization = (loginModule.state as ILoginStore)?.organization || useWorkflowManager().compiler.getState.organization
          || storageService.session.getObject(configs.ORGANIZATION);
  const organizationId = organization?.id || organization?.organizationId;
  const resultsConfig = accessSessionPayload.appConfig === 'resultsConfig';
  const testName = resultsConfig ? accessSessionPayload.results.testFlowInfo?.name : accessSessionPayload.testFlowInfo?.name;
  const offeringName = resultsConfig ? accessSessionPayload.results.offering?.name : accessSessionPayload.offering?.name;
  const accessFlowName = resultsConfig ? accessSessionPayload.results.testFlowInfo?.accessFlow : accessSessionPayload?.accessFlowName;
  const decodedJwt = decodeJwt(storageService.session.get(configs.AUTH_TOKEN));
  const userInitials = resultsConfig ? `${accessSessionPayload.personalInformation.firstName.charAt(0)}.${accessSessionPayload.personalInformation.lastName.charAt(0)}.`
    : `${personalDetails.personal_details_first_name.charAt(0)}.${personalDetails.personal_details_last_name.charAt(0)}.`;

  try {
    const event = {
      eventType,
      basicProperties: {
        organizationId: organizationId.toString(),
        organizationName: organization.name,
        organizationType: 'clinic' as ORGANIZATION_TYPE,
        userId: accessSessionPayload.userId,
        roles: Array.isArray(decodedJwt.roles) ? decodedJwt.roles.map((i: string) => i?.toLowerCase() || i) : [decodedJwt.roles.toLowerCase()],
        collectedAt: new Date().getTime(),
      },
      customEventData: {
        ...(payload?.customEventData ? payload.customEventData : {}),
        service: 'access-ui',
        access_flow_name: accessFlowName,
        clinical_indication: getRainbowClinicalIndication(accessSessionPayload.clinicalIndication),
        test: testName,
        offering: offeringName,
        access_session_id: storageService.session.get(configs.SESSION_ID_KEY),
        sample_id: accessSessionPayload.sampleId,
        triggered_by: accessSessionPayload.sampleTriggerSource || '',
        invitation_type: getFriendlyInvitationType(),
        language: accessSessionPayload.locale,
        flow_type: resultsConfig ? 'results' : 'registration',
        platform: isMobile() ? 'mobile' : 'desktop',
      },
      customOrganizationData: {
        subdomain: organization.subdomain,
      },
      customUserData: {
        $name: userInitials,
      },
    };

    if (isDevMode()) {
      return Promise.resolve('In dev mode not sending analytics events');
    }

    return sendAnalytics(
      eventType.toString(),
      event,
      {
        includeUserAgentData: true,
        includeScreenData: true,
      },
    );
  } catch (error) {
    console.error('Error sending analytics event', error);

    return Promise.resolve('Error sending analytics event');
  }
}

export default sendAnalyticsEvent;
